/**
 * Design → Image Links
 * Upload optimized images to S3 (same flow as product common images) and manage shareable URLs.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaCopy, FaImage, FaTrash, FaUpload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ConfirmModal from '../ConfirmModal';
import {
  deleteDesignImageLink,
  getDesignImageLinks,
  saveDesignImageLink,
} from '../../services/ClientService';
import { optimizeAndConvertToWebp, uploadImageToS3Bucket } from '../../services/imageOptimizerService';

const buildImageFileName = () => {
  const stamp = Date.now();
  const rand = Math.random().toString(36).slice(2, 9);
  return `image-links/${stamp}_${rand}.webp`;
};

const formatDate = (unix) => {
  if (!unix) return '—';
  return new Date(unix * 1000).toLocaleString();
};

export default function ImageLinks() {
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDesignImageLinks();
      if (res.success) {
        setImages(Array.isArray(res.data) ? res.data : []);
      } else {
        toast.error(res.message || 'Failed to load image links');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load image links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    let successCount = 0;
    try {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        setUploadProgress(`Uploading ${i + 1} of ${files.length}…`);
        const optimizedPreview = await optimizeAndConvertToWebp(file);
        const fileType = 'image/webp';
        const fileName = buildImageFileName();
        const publicUrl = await uploadImageToS3Bucket(optimizedPreview, fileType, fileName);
        if (!publicUrl) {
          toast.error(`Failed to upload ${file.name}`);
          continue;
        }
        const saveRes = await saveDesignImageLink({
          imageUrl: publicUrl,
          fileName,
          originalFileName: file.name,
        });
        if (saveRes.success) {
          successCount += 1;
        } else {
          toast.error(saveRes.message || `Failed to save ${file.name}`);
        }
      }
      if (successCount > 0) {
        toast.success(`${successCount} image${successCount > 1 ? 's' : ''} uploaded`);
        await fetchImages();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Image upload failed');
    } finally {
      setUploading(false);
      setUploadProgress('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    uploadFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith('image/'));
    uploadFiles(files);
  };

  const handleCopyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Image URL copied');
    } catch {
      toast.error('Could not copy URL');
    }
  };

  const requestDelete = (image) => {
    setImageToDelete(image);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!imageToDelete?._id) return;
    setDeletingId(imageToDelete._id);
    try {
      const res = await deleteDesignImageLink({ _id: imageToDelete._id });
      if (res.success) {
        toast.success('Image deleted');
        setImages((prev) => prev.filter((img) => img._id !== imageToDelete._id));
      } else {
        toast.error(res.message || 'Failed to delete image');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete image');
    } finally {
      setDeletingId(null);
      setConfirmOpen(false);
      setImageToDelete(null);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FaImage className="text-indigo-600" />
          Image Links
        </h1>
        <p className="text-sm text-gray-600 mt-1 max-w-2xl">
          Upload images for push notifications, campaigns, or anywhere you need a direct URL.
          Images are optimized to WebP, stored on S3, and listed here for copy or delete.
        </p>
      </div>

      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          uploading ? 'border-indigo-300 bg-indigo-50/50' : 'border-gray-300 bg-gray-50 hover:border-indigo-400 hover:bg-indigo-50/30'
        }`}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          disabled={uploading}
        />
        <FaUpload className="mx-auto text-3xl text-indigo-500 mb-3" />
        <p className="text-gray-800 font-medium">Drop images here or click to upload</p>
        <p className="text-xs text-gray-500 mt-1">Multiple files supported · Optimized to WebP · Max ~250KB each</p>
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
        >
          <FaUpload />
          {uploading ? 'Uploading…' : 'Choose images'}
        </button>
        {uploadProgress && (
          <p className="text-sm text-indigo-700 mt-3 font-medium">{uploadProgress}</p>
        )}
      </div>

      <div className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Uploaded images ({images.length})
        </h2>

        {loading ? (
          <p className="text-gray-500 text-sm">Loading…</p>
        ) : images.length === 0 ? (
          <p className="text-gray-500 text-sm py-8 text-center border border-gray-200 rounded-lg bg-white">
            No images yet. Upload your first image above.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {images.map((img) => (
              <div
                key={img._id}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm flex flex-col"
              >
                <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center overflow-hidden">
                  <img
                    src={img.imageUrl}
                    alt={img.originalFileName || 'Uploaded'}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-3 flex-1 flex flex-col gap-2">
                  <p className="text-xs text-gray-500 truncate" title={img.originalFileName}>
                    {img.originalFileName || 'image.webp'}
                  </p>
                  <p className="text-[10px] text-gray-400">
                    {formatDate(img.metaData?.createdAt)}
                  </p>
                  <p className="text-[10px] text-gray-600 break-all line-clamp-2" title={img.imageUrl}>
                    {img.imageUrl}
                  </p>
                  <div className="flex gap-2 mt-auto pt-1">
                    <button
                      type="button"
                      onClick={() => handleCopyUrl(img.imageUrl)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-semibold hover:bg-indigo-100"
                    >
                      <FaCopy size={12} />
                      Copy URL
                    </button>
                    <button
                      type="button"
                      disabled={deletingId === img._id}
                      onClick={() => requestDelete(img)}
                      className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100 disabled:opacity-50"
                    >
                      <FaTrash size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        visible={confirmOpen}
        message="This will remove the image from S3 and delete the link. Continue?"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => {
          setConfirmOpen(false);
          setImageToDelete(null);
        }}
      />
    </div>
  );
}
